// Utility for generating dynamic OG image URLs and sharing

const BASE_URL =
  process.env.REACT_APP_BASE_URL ||
  "https://obt-nearme-2025-frontend.vercel.app";

/**
 * Generate dynamic OG image URL for a specific municipality
 */
export const generateOGImageUrl = (
  tambonName: string,
  totalBudget: number,
  year: number = 2567
): string => {
  const params = new URLSearchParams({
    tambonName: tambonName,
    totalBudget: totalBudget.toString(),
    year: year.toString(),
  });

  return `${BASE_URL}/api/og?${params.toString()}`;
};

/**
 * Generate shareable URL with municipality info
 * Uses /share endpoint for proper OG tags for social crawlers
 */
export const generateShareUrl = (
  municipalityId: string,
  municipalityName: string,
  budget?: number,
  year: number = 2567
): string => {
  const params = new URLSearchParams({
    id: municipalityId,
    name: municipalityName,
    year: year.toString(),
  });

  if (budget) {
    params.set("budget", budget.toString());
  }

  // Use /share endpoint for proper OG meta tags
  return `${BASE_URL}/share?${params.toString()}`;
};

/**
 * Update meta tags for social sharing (works for client-side updates)
 * Note: This only works for browsers that re-fetch meta tags (not all crawlers)
 */
export const updateMetaTags = (
  title: string,
  description: string,
  imageUrl: string,
  url: string
): void => {
  // Update title
  document.title = title;

  // Update or create OG tags
  updateMetaTag("og:title", title);
  updateMetaTag("og:description", description);
  updateMetaTag("og:image", imageUrl);
  updateMetaTag("og:url", url);

  // Update Twitter tags
  updateMetaTag("twitter:title", title);
  updateMetaTag("twitter:description", description);
  updateMetaTag("twitter:image", imageUrl);

  // Update description
  updateMetaTag("description", description, "name");
};

/**
 * Helper to update or create a meta tag
 */
const updateMetaTag = (
  property: string,
  content: string,
  attributeName: "property" | "name" = "property"
): void => {
  let element = document.querySelector(`meta[${attributeName}="${property}"]`);

  if (element) {
    element.setAttribute("content", content);
  } else {
    element = document.createElement("meta");
    element.setAttribute(attributeName, property);
    element.setAttribute("content", content);
    document.head.appendChild(element);
  }
};

/**
 * Share content using Web Share API (if available) or fallback to clipboard
 */
export const shareContent = async (
  title: string,
  text: string,
  url: string
): Promise<{ success: boolean; method: "share" | "clipboard" | "failed" }> => {
  // Try Web Share API first (mobile-friendly)
  if (navigator.share) {
    try {
      await navigator.share({ title, text, url });
      return { success: true, method: "share" };
    } catch (error) {
      // User cancelled or error occurred
      console.log("Share cancelled or failed:", error);
    }
  }

  // Fallback to clipboard
  try {
    await navigator.clipboard.writeText(url);
    return { success: true, method: "clipboard" };
  } catch (error) {
    console.error("Failed to copy to clipboard:", error);
    return { success: false, method: "failed" };
  }
};

/**
 * Generate share text for a municipality
 */
export const generateShareText = (
  municipalityName: string,
  budget: number,
  year: number = 2567
): string => {
  const formattedBudget = budget.toLocaleString("th-TH");
  return `งบประมาณปี ${year} ของ ${municipalityName} คือ ${formattedBudget} บาท - เงินนี้ไปไหน? ตรวจสอบได้ที่ อบต.ใกล้ฉัน`;
};
